import React, { useCallback, useEffect, useState, useRef } from 'react'
import { useHistory } from 'react-router-dom'
// import moment from 'moment-timezone'
import {
  useErrorService,
  useNotificationService,
  useUserService,
} from '@abroad/components'
import {
  evaluateSlotAvailabilityItem,
  SlotAvailabilityCheckResult,
  executeSchedulingRequest,
  SchedulingResultType,
} from '@abroad/rest-client'
import { getSortedTimezones } from '../../utils/timezone'
import API from '../../utils/API'
import { BookingListView } from './index'
import ReschedulingView, { RESCHEDULE_STEPS } from './ReschedulingView'
import WeeklySessionWarningModal from './WeeklySessionWarningModal'
import AvailabilityRequiredModal, {
  SCHEDULING_REQUIREMENT_VARIANT,
} from './AvailabilityRequiredModal'
import { TAB_TYPES } from './SettingTab'
import { runSchedulingPreconditionChecks } from './schedulingPreconditions'
import { DeleteConfirmationPrompt } from '../common'
import SchedulingTabFilters from './SchedulingTabFilters'
import {
  transformEventToBooking,
  EMAIL_REGEX,
  calculateRescheduleTimes,
} from './scheduledTabHelpers'

const DEFAULT_TIMEZONE_OPTIONS = [
  { label: 'UTC', value: 'UTC' },
  { label: 'IST (UTC+05:30)', value: 'Asia/Kolkata' },
]

const RESCHEDULE_FORM_DEFAULTS = {
  name: '',
  email: '',
  message: '',
}

const INITIAL_SELECTION = {
  date: null,
  slot: null,
  timezone: DEFAULT_TIMEZONE_OPTIONS[0]?.value || 'UTC',
}

const ScheduledTab = ({
  timezone,
  isCalendarConnected,
  onFilterRender,
  highlightDate,
  highlightEventIds,
  onHighlightClear,
  prefetchedEvents,
  isPrefetchLoading,
  prefetchHasMore,
  onPrefetchLoadMore,
  skipClientFetch,
  onRescheduleSuccess,
  isComeFromClientSessions = false,
  showCoachColumn = false,
  sort = null,
  onSortChange = null,
}) => {
  const { user } = useUserService()
  const Error = useErrorService()
  const Notification = useNotificationService()
  const history = useHistory()
  const [selectedStatus, setSelectedStatus] = useState('')
  const [schedulingList, setSchedulingList] = useState([])
  const [isLoading, setIsLoading] = useState(false)
  const [isLoadingMore, setIsLoadingMore] = useState(false)
  const [selectedBooking, setSelectedBooking] = useState(null)
  const [rescheduleStep, setRescheduleStep] = useState(
    RESCHEDULE_STEPS.SELECTION,
  )
  const [rescheduleSelection, setRescheduleSelection] =
    useState(INITIAL_SELECTION)
  const [isRescheduleSubmitting, setIsRescheduleSubmitting] = useState(false)
  const [isCheckingRescheduleSlot, setIsCheckingRescheduleSlot] =
    useState(false)
  const [rescheduleSlotWeeklyModal, setRescheduleSlotWeeklyModal] = useState({
    isOpen: false,
    message: '',
  })
  const pendingRescheduleSubmitRef = useRef(false)
  const [rescheduleForm, setRescheduleForm] = useState(RESCHEDULE_FORM_DEFAULTS)
  const [guestList, setGuestList] = useState([])
  const [hasMore, setHasMore] = useState(false)
  const currentPage = useRef(1)
  const [showCancelModal, setShowCancelModal] = useState(false)
  const [bookingToCancel, setBookingToCancel] = useState(null)
  const [isCancelling, setIsCancelling] = useState(false)
  const [selectedClientId, setSelectedClientId] = useState('')
  const [isScheduleEnabled, setIsScheduleEnabled] = useState(true)
  const [hasVideoCallLink, setHasVideoCallLink] = useState(true)
  const [schedulingRequirementModal, setSchedulingRequirementModal] =
    useState(null)

  const coachId = user?._id

  const fetchSchedulingList = useCallback(
    async (loadMore = false, page = 1) => {
      // Only fetch if coachId, timezone, and calendar is connected
      if (!coachId || !timezone || !isCalendarConnected) return

      // Reset list if not loading more
      if (!loadMore) {
        setSchedulingList([])
        currentPage.current = 1
        setIsLoading(true)
      } else {
        setIsLoadingMore(true)
      }

      try {
        const queryParams = {
          limit: 20,
          page,
          status: 'upcoming',
        }
        if (selectedClientId) {
          queryParams.clientId = selectedClientId
        }

        const { data } = await API.AdminAPI.scheduling.getCoachScheduledList(
          queryParams,
        )

        if (data?.success && data?.events) {
          const transformedEvents = data.events.map((event) =>
            transformEventToBooking(event, timezone, selectedStatus),
          )
          setIsScheduleEnabled(data.isScheduleEnabled !== false)
          setHasVideoCallLink(data.hasVideoCallLink !== false)
          if (loadMore) {
            setSchedulingList((prev) => [...prev, ...transformedEvents])
          } else {
            setSchedulingList(transformedEvents)
          }

          // Set pagination state from pagination object
          const pagination = data?.pagination || {}
          const apiTotalPages = pagination.totalPages || 0
          const apiCurrentPage = pagination.page || page
          currentPage.current = apiCurrentPage
          // hasMore is true if current page is less than total pages
          setHasMore(apiCurrentPage < apiTotalPages)
        } else {
          if (!loadMore) {
            setSchedulingList([])
          }
          setHasMore(false)
        }
      } catch (error) {
        Error.showError(error)
        if (!loadMore) {
          setSchedulingList([])
        }
        setHasMore(false)
      } finally {
        if (loadMore) {
          setIsLoadingMore(false)
        } else {
          setIsLoading(false)
        }
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [coachId, selectedStatus, timezone, isCalendarConnected, selectedClientId],
  )

  // If parent provided prefetched events, use them regardless of calendar connection/timezone
  useEffect(() => {
    if (!prefetchedEvents || !Array.isArray(prefetchedEvents)) {
      return
    }

    // Show loading state when refetching (isPrefetchLoading is true)
    // This handles both initial load and refresh after reschedule
    setIsLoading(isPrefetchLoading)

    if (prefetchedEvents.length === 0 && !isPrefetchLoading) {
      setSchedulingList([])
      setHasMore(!!prefetchHasMore)
      return
    }

    if (prefetchedEvents.length > 0) {
      const transformedEvents = prefetchedEvents.map((event) =>
        transformEventToBooking(event, timezone, selectedStatus),
      )
      setSchedulingList(transformedEvents)
      setHasMore(!!prefetchHasMore)
    }
  }, [
    prefetchedEvents,
    isPrefetchLoading,
    timezone,
    selectedStatus,
    prefetchHasMore,
  ])

  useEffect(() => {
    // Skip fetching if parent is providing prefetched events (e.g., from ClientSessions)
    // This prevents duplicate API calls
    if (isComeFromClientSessions && prefetchedEvents) {
      return
    }
    // Only fetch when all conditions are met: coachId, timezone, and calendar is connected
    if (coachId && timezone && isCalendarConnected) {
      fetchSchedulingList(false)
    }
  }, [
    coachId,
    selectedStatus,
    timezone,
    isCalendarConnected,
    selectedClientId,
    fetchSchedulingList,
    isComeFromClientSessions,
    prefetchedEvents,
  ])

  const handleClientChange = useCallback((clientId) => {
    setSelectedClientId(clientId)
  }, [])

  const [filterUI, setFilterUI] = useState(null)

  const handleFilterRender = useCallback(
    (ui) => {
      setFilterUI(ui)
      if (onFilterRender) {
        onFilterRender(ui)
      }
    },
    [onFilterRender],
  )

  const handleLoadMore = useCallback(async () => {
    if (onPrefetchLoadMore && prefetchHasMore) {
      if (isLoading || isLoadingMore) return
      setIsLoadingMore(true)
      try {
        await onPrefetchLoadMore()
      } finally {
        setIsLoadingMore(false)
      }
      return
    }

    if (hasMore && !isLoading && !isLoadingMore) {
      fetchSchedulingList(true, currentPage.current + 1)
    }
  }, [
    hasMore,
    isLoading,
    isLoadingMore,
    fetchSchedulingList,
    onPrefetchLoadMore,
    prefetchHasMore,
  ])

  const resetRescheduleState = useCallback(() => {
    setRescheduleStep(RESCHEDULE_STEPS.SELECTION)
    setRescheduleSelection(INITIAL_SELECTION)
    setRescheduleForm(RESCHEDULE_FORM_DEFAULTS)
    setRescheduleSlotWeeklyModal({ isOpen: false, message: '' })
    setGuestList([])
  }, [])

  const handleStatusChange = (eventOrValue) => {
    if (!eventOrValue) {
      setSelectedStatus('')
      return
    }

    if (typeof eventOrValue === 'string') {
      setSelectedStatus(eventOrValue)
      return
    }

    if (eventOrValue?.target) {
      setSelectedStatus(eventOrValue.target.value)
      return
    }

    setSelectedStatus(eventOrValue?.value || '')
  }

  const handleRescheduleClick = (booking) => {
    if (!booking) return

    // // Check if session is within 24 hours (for conditional scheduling QUEST sessions)
    // if (isWithin24Hours(booking)) {
    //   // Don't proceed with reschedule if within 24 hours
    //   return
    // }

    const canProceed = runSchedulingPreconditionChecks({
      hasVideoCallLink,
      isScheduleEnabled,
      onRequireVideoCallLink: () =>
        setSchedulingRequirementModal(
          SCHEDULING_REQUIREMENT_VARIANT.VIDEO_CALL_LINK,
        ),
      onRequireAvailability: () =>
        setSchedulingRequirementModal(
          SCHEDULING_REQUIREMENT_VARIANT.AVAILABILITY,
        ),
    })
    if (!canProceed) {
      return
    }

    const defaultTimezone = booking?.timeZone || INITIAL_SELECTION.timezone

    setRescheduleSelection({
      date: null,
      slot: null,
      timezone: defaultTimezone,
    })
    setRescheduleForm({
      name: booking?.clientName || '',
      // email: booking?.client?.email || booking?.clientEmail || '',
      message: '',
    })
    // Initialize guest list from booking attendees
    const initialAttendees =
      booking?.attendees || booking?.guests || booking?.guestEmails || []
    setGuestList(Array.isArray(initialAttendees) ? [...initialAttendees] : [])
    setRescheduleStep(RESCHEDULE_STEPS.SELECTION)
    setSelectedBooking(booking)
  }

  const handleCancelClick = (booking) => {
    if (!booking || !coachId) return

    // // Check if session is within 24 hours (for conditional scheduling QUEST sessions)
    // if (isWithin24Hours(booking)) {
    //   // Don't proceed with cancel if within 24 hours
    //   return
    // }

    const eventId = booking?._id || booking?.id
    if (!eventId) {
      Notification.showNotification('Event ID is missing', 'danger')
      return
    }

    setBookingToCancel(booking)
    setShowCancelModal(true)
  }

  const handleConfirmCancel = async () => {
    if (!bookingToCancel || !coachId) return

    const eventId = bookingToCancel?._id || bookingToCancel?.id
    if (!eventId) {
      Notification.showNotification('Event ID is missing', 'danger')
      setShowCancelModal(false)
      setBookingToCancel(null)
      return
    }

    setIsCancelling(true)
    try {
      await API.AdminAPI.scheduling.cancelBooking(coachId, eventId)
      Notification.showNotification(
        'Booking cancelled successfully.',
        'success',
      )
      setShowCancelModal(false)
      setBookingToCancel(null)
      // Refresh the booking list. Inside a client profile the list is fed by the
      // parent (client-filtered), so let it refetch instead of this unfiltered call.
      if (isComeFromClientSessions && onRescheduleSuccess) {
        try {
          await onRescheduleSuccess()
        } catch (e) {
          // ignore handler errors
        }
      } else {
        fetchSchedulingList(false)
      }
    } catch (error) {
      Error.showError(error)
    } finally {
      setIsCancelling(false)
    }
  }

  const handleBackToList = () => {
    resetRescheduleState()
    setSelectedBooking(null)
  }

  const closeSchedulingRequirementModal = () => {
    setSchedulingRequirementModal(null)
  }

  const handleSchedulingRequirementConfirm = () => {
    setSchedulingRequirementModal(null)

    if (
      schedulingRequirementModal ===
      SCHEDULING_REQUIREMENT_VARIANT.VIDEO_CALL_LINK
    ) {
      history.push('/coach/scheduling/zoom')
      return
    }

    history.push('/coach/scheduling/setting', {
      settingTab: TAB_TYPES.AVAILABILITY,
    })
  }

  const handleRescheduleFieldChange = (field, value) => {
    setRescheduleForm((prev) => ({
      ...prev,
      [field]: value,
    }))
  }

  const handleAddGuest = () => {
    // Simply add a new empty input field to the list
    setGuestList((prev) => [...prev, ''])
  }

  const handleRemoveGuest = (index) => {
    setGuestList((prev) => prev.filter((_, i) => i !== index))
  }

  const handleGuestUpdate = (index, newEmail) => {
    setGuestList((prev) =>
      prev.map((email, i) => (i === index ? newEmail : email)),
    )
  }

  const handleDateChange = (dateISO) => {
    setRescheduleSelection((prev) => ({
      ...prev,
      date: dateISO,
    }))
  }

  const handleSlotSelect = ({ date, slot, timezone }) => {
    setRescheduleSelection({
      date,
      slot,
      timezone: timezone || rescheduleSelection.timezone,
    })
  }

  const getRescheduleTimezone = useCallback(() => {
    let timezoneToUse = timezone || null
    if (!timezoneToUse) {
      const userTimezone = user?.timezone
      let browserTimezone = ''
      try {
        if (
          typeof Intl !== 'undefined' &&
          Intl.DateTimeFormat &&
          Intl.DateTimeFormat().resolvedOptions
        ) {
          browserTimezone =
            Intl.DateTimeFormat().resolvedOptions().timeZone || ''
        }
      } catch (e) {
        browserTimezone = ''
      }
      timezoneToUse = userTimezone || browserTimezone || 'Asia/Kolkata'
    }
    const timezoneOptions = getSortedTimezones()
    if (!timezoneOptions.some((tz) => tz.value === timezoneToUse)) {
      timezoneToUse = 'Asia/Kolkata'
    }
    return timezoneToUse
  }, [timezone, user?.timezone, user?.isCoach])

  const checkRescheduleSlotAvailability = useCallback(
    async (selection, booking) => {
      if (!coachId || !selection?.date || !selection?.slot || !booking) {
        return { allowed: false }
      }

      const timezoneToUse = getRescheduleTimezone()
      const times = calculateRescheduleTimes(selection, booking, timezoneToUse)
      if (!times?.slotStart) {
        return { allowed: false }
      }

      const eventId = booking?._id || booking?.id
      const payload = {
        selectedSlots: [times.slotStart],
        sowSessionDuration: Number(booking?.duration) || 0,
        ...(booking?.sowId ? { sowId: booking.sowId } : {}),
        ...(eventId ? { eventId } : {}),
      }

      const { data } = await API.ClientAPI.session.checkSlotAvailability(
        coachId,
        payload,
      )

      if (!data?.success || !Array.isArray(data.data) || !data.data[0]) {
        return { allowed: true }
      }

      return evaluateSlotAvailabilityItem(data.data[0])
    },
    [coachId, getRescheduleTimezone],
  )

  const proceedToRescheduleDetails = useCallback((selection) => {
    setRescheduleSelection(selection)
    setRescheduleStep(RESCHEDULE_STEPS.DETAILS)
  }, [])

  const handleScheduleSelectionComplete = async ({
    date,
    slot,
    timezone: selectionTimezone,
  }) => {
    const selection = {
      date,
      slot,
      timezone: selectionTimezone || rescheduleSelection.timezone,
    }

    setRescheduleSelection(selection)

    setIsCheckingRescheduleSlot(true)
    try {
      const result = await checkRescheduleSlotAvailability(
        selection,
        selectedBooking,
      )

      if (result.type === SlotAvailabilityCheckResult.HARD_BLOCK) {
        Notification.showNotification(
          result.message ||
            'Unable to reschedule to this time. Please choose another slot.',
          'danger',
        )
        return
      }

      if (result.type === SlotAvailabilityCheckResult.UNAVAILABLE) {
        Notification.showNotification(
          result.message ||
            'Coach is not available at this time. Please choose another slot.',
          'danger',
        )
        return
      }

      if (result.type === SlotAvailabilityCheckResult.WEEKLY_WARNING) {
        setRescheduleSlotWeeklyModal({
          isOpen: true,
          message: result.message,
        })
        return
      }

      proceedToRescheduleDetails(selection)
    } catch (error) {
      Error.showError(error)
    } finally {
      setIsCheckingRescheduleSlot(false)
    }
  }

  const handleEditTime = () => {
    setRescheduleStep(RESCHEDULE_STEPS.SELECTION)
  }

  // // Check if session is within 24 hours (for conditional scheduling QUEST sessions)
  // const isWithin24Hours = (booking) => {
  //   if (!booking?.startDateTime) {
  //     return false
  //   }

  //   const sowType = booking?.sowType
  //   const isConditionalSchedulingEnabled =
  //     booking?.isConditionalSchedulingEnabled || false

  //   // Only check for QUEST sessions with conditional scheduling enabled
  //   if (sowType !== 'QUEST' || !isConditionalSchedulingEnabled) {
  //     return false
  //   }

  //   const sessionStart = moment.utc(booking.startDateTime)
  //   const now = moment.utc()
  //   const hoursUntilSession = sessionStart.diff(now, 'hours', true)

  //   // Return true if session is within 24 hours (less than 24 hours away)
  //   return hoursUntilSession > 0 && hoursUntilSession <= 24
  // }

  const handleRescheduleSubmit = async () => {
    const bookingId = selectedBooking?._id || selectedBooking?.id
    if (
      !bookingId ||
      !rescheduleSelection?.date ||
      !rescheduleSelection?.slot ||
      !coachId
    ) {
      return
    }

    // Determine timezone to use for rescheduling: prefer prop, then coach API, then user/browser, then fallback
    let timezoneToUse = timezone || null
    if (!timezoneToUse) {
      if (user?.isCoach) {
        try {
          const { data: tzData } = await API.CoachAPI.coachUser.getTimezone()
          if (tzData?.timezone) {
            timezoneToUse = tzData.timezone
          }
        } catch (err) {
          // ignore and fallback
        }
      }
    }
    if (!timezoneToUse) {
      const userTimezone = user?.timezone
      let browserTimezone = ''
      try {
        if (
          typeof Intl !== 'undefined' &&
          Intl.DateTimeFormat &&
          Intl.DateTimeFormat().resolvedOptions
        ) {
          browserTimezone =
            Intl.DateTimeFormat().resolvedOptions().timeZone || ''
        }
      } catch (e) {
        browserTimezone = ''
      }
      timezoneToUse = userTimezone || browserTimezone || 'Asia/Kolkata'
    }

    // Validate timezone
    const timezoneOptions = getSortedTimezones()
    if (!timezoneOptions.some((tz) => tz.value === timezoneToUse)) {
      timezoneToUse = 'Asia/Kolkata'
    }

    const times = calculateRescheduleTimes(
      rescheduleSelection,
      selectedBooking,
      timezoneToUse,
    )
    if (!times) {
      return
    }

    // Filter out empty emails and get unique, valid emails
    const attendees = guestList
      .map((email) => email?.trim())
      .filter((email) => email && EMAIL_REGEX.test(email))
      .filter((email, index, self) => {
        // Remove duplicates (case-insensitive)
        const lower = email.toLowerCase()
        return self.findIndex((item) => item.toLowerCase() === lower) === index
      })

    const payload = {
      eventId: bookingId,
      slotStart: times.slotStart,
      slotEnd: times.slotEnd,
      attendees,
      reason: rescheduleForm.message,
    }

    setIsRescheduleSubmitting(true)
    try {
      const result = await executeSchedulingRequest(
        (schedulePayload) =>
          API.ClientAPI.session.rescheduleSession(coachId, schedulePayload),
        payload,
      )

      if (result.type === SchedulingResultType.SAME_DAY_CONFLICT) {
        Notification.showNotification(result.message, 'danger')
        setRescheduleStep(RESCHEDULE_STEPS.SELECTION)
        return
      }

      if (result.type === SchedulingResultType.WEEKLY_WARNING) {
        pendingRescheduleSubmitRef.current = true
        setRescheduleSlotWeeklyModal({
          isOpen: true,
          message: result.message,
        })
        return
      }

      pendingRescheduleSubmitRef.current = false

      if (result.type !== SchedulingResultType.SUCCESS) {
        Notification.showNotification(
          result.message ||
            'Unable to reschedule this session. Please try again.',
          'danger',
        )
        return
      }

      const data = result.data

      Notification.showNotification(
        data?.message || 'Session rescheduled successfully.',
        'success',
      )
      resetRescheduleState()
      setSelectedBooking(null)

      // If using prefetched events (isComeFromClientSessions), call onRescheduleSuccess to refetch
      // Otherwise, refresh the local list
      if (isComeFromClientSessions && onRescheduleSuccess) {
        try {
          await onRescheduleSuccess()
        } catch (e) {
          // ignore handler errors
        }
      } else {
        fetchSchedulingList(false)
        if (onRescheduleSuccess) {
          try {
            onRescheduleSuccess()
          } catch (e) {
            // ignore handler errors
          }
        }
      }
    } catch (error) {
      Error.showError(error)
    } finally {
      setIsRescheduleSubmitting(false)
    }
  }

  return (
    <>
      <SchedulingTabFilters
        selectedClientId={selectedClientId}
        onClientChange={handleClientChange}
        onFilterRender={handleFilterRender}
        isLoading={isLoading}
        disabled={!!selectedBooking}
        skipClientFetch={skipClientFetch}
      />
      {selectedBooking ? (
        <ReschedulingView
          booking={selectedBooking}
          coachId={coachId}
          rescheduleStep={rescheduleStep}
          rescheduleSelection={rescheduleSelection}
          onBackToList={handleBackToList}
          onDateChange={handleDateChange}
          onSlotSelect={handleSlotSelect}
          onSelectionComplete={handleScheduleSelectionComplete}
          onEditTime={handleEditTime}
          onSubmit={handleRescheduleSubmit}
          isSubmitting={isRescheduleSubmitting}
          rescheduleForm={rescheduleForm}
          onRescheduleFieldChange={handleRescheduleFieldChange}
          guestList={guestList}
          onAddGuest={handleAddGuest}
          onRemoveGuest={handleRemoveGuest}
          onGuestUpdate={handleGuestUpdate}
          timezone={timezone}
          isCheckingAvailability={isCheckingRescheduleSlot}
        />
      ) : (
        <>
          <BookingListView
            selectedStatus={selectedStatus}
            onStatusChange={handleStatusChange}
            schedulingList={schedulingList}
            isLoading={isLoading}
            isLoadingMore={isLoadingMore}
            onRescheduleClick={handleRescheduleClick}
            onCancelClick={handleCancelClick}
            hasMore={hasMore}
            onLoadMore={handleLoadMore}
            timezone={timezone}
            clearFilter={filterUI?.clearFilter}
            highlightDate={highlightDate}
            highlightEventIds={highlightEventIds}
            onHighlightClear={onHighlightClear}
            // isWithin24Hours={isWithin24Hours}
            isComeFromClientSessions={isComeFromClientSessions}
            isCalendarConnected={isCalendarConnected}
            showCoachColumn={showCoachColumn}
            sort={sort}
            onSortChange={onSortChange}
          />
          <DeleteConfirmationPrompt
            showModal={showCancelModal}
            setShowModal={setShowCancelModal}
            isDeleteLoading={isCancelling}
            handleDelete={handleConfirmCancel}
            message={
              bookingToCancel
                ? `Are you sure you want to cancel the booking for ${
                    bookingToCancel?.clientName || 'this booking'
                  }? This action cannot be undone.`
                : 'Are you sure you want to cancel this booking? This action cannot be undone.'
            }
            buttonConfirmText='Remove'
          />
          <AvailabilityRequiredModal
            show={Boolean(schedulingRequirementModal)}
            variant={schedulingRequirementModal}
            handleClose={closeSchedulingRequirementModal}
            onConfirm={handleSchedulingRequirementConfirm}
          />
        </>
      )}
      <WeeklySessionWarningModal
        isOpen={rescheduleSlotWeeklyModal.isOpen}
        message={rescheduleSlotWeeklyModal.message}
        onConfirm={() => {
          setRescheduleSlotWeeklyModal({ isOpen: false, message: '' })
          if (pendingRescheduleSubmitRef.current) {
            pendingRescheduleSubmitRef.current = false
            handleRescheduleSubmit()
            return
          }
          proceedToRescheduleDetails(rescheduleSelection)
        }}
        onCancel={() => {
          pendingRescheduleSubmitRef.current = false
          setRescheduleSlotWeeklyModal({ isOpen: false, message: '' })
          if (rescheduleStep === RESCHEDULE_STEPS.SELECTION) {
            setRescheduleSelection((prev) => ({
              ...prev,
              slot: null,
            }))
          }
        }}
        loading={isRescheduleSubmitting || isCheckingRescheduleSlot}
      />
    </>
  )
}

export default ScheduledTab
